<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-1">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" option="Last 30 days" hide-details/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Product units" dense outlined :items="unitsList" v-model="units" hide-details @change="refresh()"/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Show customer" dense outlined :items="['Name','Type','Category']" v-model="by" hide-details @change="refresh()"/>
        </v-col>
        <v-col cols="4">
          <multi-level-select :menuData="customerMenu" :first.sync="type" :second.sync="customerId" outlined label="Customer" @change="refresh()" hide-details/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Sales details by item',report)"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="2">
          <v-select label="Source" hide-details outlined dense class="mt-1" v-model="source" :items="[{text:'Shipments',value:'shipment'},{text:'Invoices',value:'invoice'},{text:'Sales orders',value:'salesOrder'}]"  @change="refresh()"/>
        </v-col>
        <v-col cols="3" v-if="source==='shipment' && sites.length>1">
          <v-autocomplete label="Site" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="siteId" item-text="name" item-value="id" @change="refresh()" class="mt-1"/>
        </v-col>
        <v-col cols="3" v-if="salesReps.length>1">
          <v-autocomplete label="Sales rep" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...salesReps]" v-model="salesRepId" item-text="name" item-value="id" @change="refresh()" class="mt-1"/>
        </v-col>
        <v-col cols="2" :offset="source==='shipment' && sites.length>1 ? 0 : 1">
          <v-switch label="Exclude $0 sales" hide-details dense class="mt-2" v-model="exclude0" @change="refresh()"/>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 228px)">
      <span v-html="report"/>
    </v-card>
  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'
import customerTypes from '@/mixins/customerTypes'

export default {
  name: 'ReportsSalesDetailsByItem',
  mixins: [customerTypes,printHtml],
  data() {
    return {
      type: '0',
      by: 'Name',
      customerId: null,
      salesRepId: null,
      from: null,
      to: null,
      dateDisplay: '',
      unitsList: [{text:'bottles',value:'bottles'},{text:'units',value:'sellingUnits'},
                  {text:'cases',value:'cases'},{text:'pallets',value:'pallets'},
                  {text:'litres',value:'litres'},{text:'laa',value:'laa'}],
      precisionList: {bottles:0, sellingUnits:0, cases:1, pallets:1, litres:3, laa:3},
      exclude0: false,
      source: 'shipment',
      units: 'bottles',
      siteId: null,
      loading: true,
      previousItem: null,
      totalQuantity: 0,
      totalValue: 0,
      precision: 0,
      lineUnits: '',
      product: false,
      lines: [],
      customers: [],
      salesReps: [],
      types: [],
      categories: [],
      customerMenu: [{name:'All',id:'0'},{divider:true}],
      report: ''
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true);
    },
    title() {
      const title = `Sales details by item`;
      if (this.type === '0')
        return title;
      if (this.type === '1')
        return `${title} for ${this.customers.find(c => c.id === this.customerId).name}`;
      if (this.type === '2')
        return `${title} for ${this.types.find(c => c.id === this.customerId).name}`;
      return `${title} for ${this.categories.find(c => c.id === this.customerId).name}`;
    }
  },
  async created() {
    this.$emit('name', 'Sales details by item')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
    this.customerId = this.$route.query.customerId ?? null;
    this.type = this.$route.query.customerId ? '1' : '0';

    this.customers = await this.$store.dispatch('customers', true);
    this.types = (await this.buildCustomerTypes()).map(type => ({name:type.text,id:type.value})).filter(type => type.id);
    this.categories = [];
    await this.$lazyListLoad(this.categories, 'categories2', 'id,name', 100, [], [{name:'type',value:'Customer'}]),
    await this.$lazyListLoad(this.salesReps, 'salesReps2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),

    this.customerMenu.push({name:'Name',id:'1',menu:this.customers});
    this.customerMenu.push({name:'Type',id:'2',menu:this.types});
    if (this.categories.length)
      this.customerMenu.push({name:'Category',id:'3',menu:this.categories});
  },
  methods: {
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsSalesDetailsByItem($from:Date!,$to:Date!,$customerId:ID, $customerType:CustomerType, $customerCategoryId:ID, $salesRepId:ID, $exclude0:Boolean, $source:String!, $siteId:ID, $by:String) {
                                              salesDetailsReportByItem(from:$from,to:$to,customerId:$customerId,customerType:$customerType,customerCategoryId:$customerCategoryId,salesRepId:$salesRepId,exclude0:$exclude0,source:$source,siteId:$siteId,by:$by) {
                                                name,category,product,units,precision
                                                quantity,bottles,sellingUnits,cases,pallets,litres,laa,value
                                                customer,date,invoiceNumber,lot
                                              }
                                            }`,
                                          {from:this.from,to:this.to,
                                           customerId:this.type==='1'?this.customerId:null,
                                           customerType:this.type==='2'?this.customerId:null,
                                           customerCategoryId:this.type==='3'?this.customerId:null,
                                           salesRepId:this.salesRepId,
                                           exclude0:this.exclude0,source:this.source,siteId:this.siteId,
                                           by:this.by});
      if (!results.error)
        this.lines = results.data.salesDetailsReportByItem;

      const lines = [
                  `<style>
                    @page {size:landscape}
                    .report {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .report th {height:36px;padding:0 16px;vertical-align:middle}
                    .report .spacer {height:18px}
                    .report td {height:36px;padding:0 16px;vertical-align:middle}
                    .report .title {font-size:18px;text-transform:uppercase}
                    .report .sup-title {font-weight:normal;font-size:22px}
                    .report .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                    .report .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .report .text-left {text-align:left}
                    .report .text-right {text-align:right}
                    .report .indent {text-indent:20px}
                    .report .font-weight-bold {font-weight:bold}
                    .report .total-row {font-weight:bold}
                  </style>
                   <table class="report">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="6" class="title">${this.title}</th>
                        </tr>
                        <tr>
                           <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                        </tr>
                        <tr class="header">
                          <th width="30%" class="text-left">Item/${this.by==='Name'?'Customer':this.by}</th>
                          ${this.source === 'salesOrder' ? ``
                                                        : `<th width="10%" class="text-left">Lot</th>
                                                           <th width="15%" class="text-left">Invoice #</th>`}
                          <th width="15%" class="text-left">Date</th>
                          <th width="15%" class="text-right">Quantity</th>
                          <th width="15%" class="text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody>`
      ];
      this.previousItem = null;
      for (const line of this.lines) {
        if (line.name !== this.previousItem) {
          this.totalLine(lines);
          lines.push(`<tr><td colspan="${this.source === 'salesOrder' ? 2 : 4}">${line.name}</td></tr>`);
          this.previousItem = line.name;
        }
        lines.push(`<tr>
                      <td class="indent">${line.customer}</td>`);
        if (this.source !== 'salesOrder')
          lines.push(`  <td>${line.lot ?? ''}</td>
                        <td>${line.invoiceNumber ?? ''}</td>`);
        lines.push(`  <td>${this.$formatDateTime(line.date)}</td>
                      <td class="text-right">`);
        if (line.product) {
          const precision = this.precisionList[this.units] ?? line.precision;
          lines.push(`${this.$number(line[this.units],precision)} ${this.unitsList.find(unit => unit.value === this.units).text}`);
          this.totalQuantity += line[this.units];
        }
        else {
          lines.push(`${this.$number(line.quantity,line.precision)} ${line.units}`);
          this.totalQuantity += line.quantity;
        }
        lines.push(`</td>
                    <td class="text-right">$${this.$number(line.value,2)}</td>
                    </tr>`);

        this.totalValue += line.value;
        this.precision = line.precision;
        this.lineUnits = line.units;
        this.product = line.product;
      }
      this.totalLine(lines);

      lines.push(`</tbody>`);
      lines.push(`</table>`);
      this.report = lines.join('');
      this.loading = false;
    },
    totalLine(lines) {
      if (!this.previousItem)
        return;

      lines.push(`<tr class="total-row"><td colspan="${this.source === 'salesOrder' ? 3 : 5}" class="text-right">`);
      if (this.product)
        lines.push(`${this.$number(this.totalQuantity,this.precision)} ${this.unitsList.find(unit => unit.value === this.units).text}`);
      else
        lines.push(`${this.$number(this.totalQuantity,this.precision)} ${this.lineUnits}`);
      lines.push(`</td>
                  <td class="text-right">$${this.$number(this.totalValue,2)}</td>
                  </tr>`);
      this.totalValue = 0;
      this.totalQuantity = 0;
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,,,`,
                      `"Sales by item",,,,,,`,
                      `"${this.dateDisplay}",,,,,,`,
                      `"Item","Category","${this.by==='Name'?'Customer':this.by}","Lot","Invoice #","Date","Quantity","Value"`
                    ];
      for (const line of this.lines) {
        const precision = (line.product ? this.precisionList[this.units] : null) ?? line.precision;
        const quantity = line.product ? `${this.$number(line[this.units],precision)}` :
                                        `${this.$number(line.quantity,precision)}`;
        lines.push(`"${line.name}","${line.category??''}","${line.customer}","${line.lot??''}","${line.invoiceNumber??''}","${line.date}","${quantity}","${this.$number(line.value,2)}"`);
      }

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Sales by item.csv');
    }
  }
}
</script>